import { useEffect, useMemo, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { TbPlus, TbX } from 'react-icons/tb';
import styles from './styles.module.css';

const createEmptySlots = () => ['', '', '', '', ''];

const isValidHexColor = (value) =>
  /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(String(value || '').trim());

function slotsFromColors(colors = []) {
  const slots = createEmptySlots();
  colors.filter(Boolean).forEach((color, index) => {
    if (index < slots.length) {
      slots[index] = color;
    }
  });
  return slots;
}

export const CampaignCollorPallete = ({
  onConfirmPalette,
  embedded = false,
  isReadOnly = false,
  initialColors = [],
  title = 'Monte a paleta da sua marca',
  subtitle = 'Toque em um slot e escolha a cor — ela aplica na hora. Adicione de 3 a 5 cores.',
  confirmLabel = 'Salvar paleta e continuar',
}) => {
  const colorPickerRef = useRef(null);
  const [slots, setSlots] = useState(() => slotsFromColors(initialColors));
  const [activeSlot, setActiveSlot] = useState(0);
  const [currentHex, setCurrentHex] = useState(initialColors[0] || '');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [neonSlotIndex, setNeonSlotIndex] = useState(null);

  useEffect(() => {
    if (initialColors.length > 0) {
      const nextSlots = slotsFromColors(initialColors);
      setSlots(nextSlots);
      const firstColor = nextSlots.find(Boolean);
      if (firstColor) {
        setCurrentHex(firstColor);
      }
    }
  }, [initialColors]);

  const moveNeonToNextSlot = (slotIndex = activeSlot) => {
    const nextSlotIndex = slotIndex + 1;
    if (slots[slotIndex] && nextSlotIndex < slots.length && !slots[nextSlotIndex]) {
      setNeonSlotIndex(nextSlotIndex);
      return;
    }
    setNeonSlotIndex(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isPickerOpen) return;
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target)
      ) {
        setIsPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPickerOpen]);

  useEffect(() => {
    if (!isPickerOpen) {
      moveNeonToNextSlot();
    }
  }, [isPickerOpen, activeSlot, slots]);

  const colors = useMemo(() => slots.filter(Boolean), [slots]);

  const handleActivateSlot = (index) => {
    if (isReadOnly) return;
    if (neonSlotIndex === index) setNeonSlotIndex(null);
    setActiveSlot(index);
    if (slots[index]) setCurrentHex(slots[index]);
    setIsPickerOpen(true);
  };

  const handlePickerChange = (nextColor) => {
    setCurrentHex(nextColor);
    setSlots((current) => {
      const next = [...current];
      next[activeSlot] = nextColor;
      return next;
    });
  };

  const handleHexInputChange = (event) => {
    const nextHex = event.target.value;
    setCurrentHex(nextHex);
    if (!isValidHexColor(nextHex)) return;
    setSlots((current) => {
      const next = [...current];
      next[activeSlot] = nextHex;
      return next;
    });
  };

  const handleRemove = (event, index) => {
    event.stopPropagation();
    setSlots((current) => {
      const next = [...current];
      next[index] = '';
      return next;
    });
    setActiveSlot(index);
    setCurrentHex('#7c3aed');
  };

  const countLabel = `${colors.length} de ${slots.length}`;

  const handleConfirmPalette = () => {
    onConfirmPalette?.(colors);
  };

  const slotsToRender = isReadOnly
    ? colors.map((color, index) => ({ color, originalIndex: index }))
    : slots.map((color, index) => ({ color, originalIndex: index }));

  const isSaveActive = colors.length >= 3;

  const pickerCard = (
    <div className={styles.pickerCard}>
      <div className={styles.cardHeader}>
        <div className={styles.cardIcon}>
          <div className={styles.cardIconWheel} aria-hidden="true" />
        </div>
        <div className={styles.cardTitles}>
          <div className={styles.cardTitle}>{title}</div>
          <div className={styles.cardSubtitle}>{subtitle}</div>
        </div>
      </div>

      <div className={styles.labelRow}>
        <span className={styles.labelText}>Slots da paleta</span>
        <span className={styles.countText}>{countLabel}</span>
      </div>

      <div className={styles.paletteSlots}>
        {slotsToRender.map(({ color, originalIndex: index }) => {
          const isEmpty = !color;
          if (isReadOnly && isEmpty) return null;

          const isActive = activeSlot === index;
          const isNeon = isEmpty && neonSlotIndex === index;

          const swatchClasses = [
            styles.swatch,
            isEmpty && !isActive ? styles.swatchEmpty : '',
            isEmpty && isActive ? styles.neonPulse : '',
            !isEmpty && isActive ? styles.swatchFilledActive : '',
            isNeon ? styles.neonPulse : '',
            isReadOnly ? styles.slotReadOnly : '',
          ].join(' ');

          const labelClasses = isActive && isEmpty
            ? styles.slotLabelActive
            : styles.slotLabelDefault;

          return (
            <div className={styles.slotColumn} key={`slot-${index}`}>
              <div
                className={swatchClasses}
                onClick={() => handleActivateSlot(index)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleActivateSlot(index);
                  }
                }}
                role="button"
                aria-label={
                  isEmpty
                    ? `Slot P${index + 1}, vazio. Clique para escolher uma cor.`
                    : `Slot P${index + 1}, cor ${color}. Clique para editar.`
                }
                style={color ? { background: color } : undefined}
                tabIndex={isReadOnly ? -1 : 0}
              >
                {!isEmpty && !isReadOnly && (
                  <button
                    className={styles.slotRemove}
                    onClick={(event) => handleRemove(event, index)}
                    type="button"
                    aria-label={`Remover cor do slot P${index + 1}`}
                  >
                    <TbX aria-hidden="true" size={10} />
                  </button>
                )}
                {isEmpty && (
                  <TbPlus
                    aria-hidden="true"
                    className={styles.swatchEmptyIcon}
                    size={14}
                  />
                )}
              </div>
              <span className={`${styles.slotLabel} ${labelClasses}`}>
                {`P${index + 1}`}
              </span>

            </div>
          );
        })}
      </div>

      {isPickerOpen && !isReadOnly && (
        <div
          className={styles.colorPickerPopover}
          onMouseUp={() => moveNeonToNextSlot(activeSlot)}
          onPointerUp={() => moveNeonToNextSlot(activeSlot)}
          ref={colorPickerRef}
        >
          <div className={styles.popoverHeader}>
            <div className={styles.popoverTitle}>
              <span className={styles.popoverTitleText}>Cor do slot</span>
              <span className={styles.popoverChip}>{`P${activeSlot + 1}`}</span>
            </div>
            <button
              className={styles.popoverClose}
              onClick={() => setIsPickerOpen(false)}
              type="button"
              aria-label="Fechar seletor de cor"
            >
              <TbX size={14} />
            </button>
          </div>

          <div className={styles.popoverPickerWrap}>
            <HexColorPicker
              color={currentHex}
              onChange={handlePickerChange}
            />
            <button
              className={styles.popoverEyedropper}
              type="button"
              aria-label="Conta-gotas"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0L12 2.69z" stroke="#414141" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <div className={styles.popoverHexRow}>
            <span className={styles.popoverHexLabel}>HEX</span>
            <div className={styles.popoverHexField}>
              <span className={styles.popoverHexHash}>#</span>
              <input
                className={styles.popoverHexInput}
                onChange={handleHexInputChange}
                placeholder="2FCC70"
                type="text"
                value={currentHex.replace('#', '')}
                aria-label="Código hexadecimal da cor"
              />
            </div>
          </div>
        </div>
      )}

      {!isReadOnly && (
        <div className={styles.actions}>
          <button
            className={`${styles.saveBtn} ${isSaveActive ? styles.saveBtnActive : ''}`}
            onClick={handleConfirmPalette}
            type="button"
            disabled={!isSaveActive}
          >
            {confirmLabel}
          </button>
        </div>
      )}

      {!isReadOnly && (
        <div className={styles.hint}>
          <span className={styles.hintText}>Mínimo de 3 cores para continuar</span>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return <div className={styles.embeddedRoot}>{pickerCard}</div>;
  }

  return (
    <section className={styles.root}>
      <h2 className={styles.srOnly}>
        Protótipo de seleção de múltiplas cores no chat
      </h2>
      <div className={styles.panel}>
        <div className={styles.chatWrapper}>
          <div className={styles.chatWindow}>
            <div className={styles.chatBody}>
              <div className={`${styles.msg} ${styles.bot}`}>{pickerCard}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
